import React, { useState, useRef, useEffect } from "react";
import {
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import SportImgMap from "./SportImgMap";

const ProfilePassionsGrid = ({ passions, showPassions }) => {
  const [expanded, setExpanded] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const gridRef = useRef(null);

  // Show all passions when expanded, otherwise limit to 5
  const displayPassions = expanded ? passions : (window.innerWidth <= 990 ? passions?.slice(0, 3) : passions?.slice(0, 4));
  const hasMorePassions = passions?.length >= 5;

  // Check if scrolling is possible
  useEffect(() => {
    if (!gridRef.current) return;

    const checkScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = gridRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    };

    checkScroll();
    gridRef.current.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      if (gridRef.current) {
        gridRef.current.removeEventListener("scroll", checkScroll);
      }
      window.removeEventListener("resize", checkScroll);
    };
  }, [passions, expanded]);

  const scrollGrid = (direction) => {
    if (!gridRef.current) return;

    const scrollAmount = direction === "left" ? -200 : 200;
    gridRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
  };

  if (!showPassions || !passions?.length) return null;

  return (
    <div className="w-full py-3">
      {/* Grid container with responsive columns */}
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold">Passions</h3>
        
        <div className="flex items-center gap-2">
  {/* Expand/Collapse button (Visible on all screen sizes) */}
  {hasMorePassions && (
    <button 
      onClick={() => setExpanded(!expanded)}
      className="flex items-center text-sm text-gray-600 hover:text-gray-800"
    >
      {expanded ? (
        <>
          View Less <ChevronUp className="w-4 h-4 ml-1" />
        </>
      ) : (
        <>
          View All <ChevronDown className="w-4 h-4 ml-1" />
        </>
      )}
    </button>
  )}
</div>

      </div>
      <div 
        className={`grid grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-3 justify-items-center`}
        style={{ justifyContent: passions?.length < 5 ? 'space-between' : 'center' }}
      >
        {displayPassions?.map((passion) => (
          <div
            key={passion._id}
            className="flex flex-col items-center p-2 border rounded-lg hover:shadow-md transition-shadow min-w-24"
          >
            {/* Image container */}
            <div className="w-16 h-16 md:w-20 md:h-20 mb-2">
              <img
                src={SportImgMap[passion.name.split(" ")[0]]}
                alt={passion.name}
                className="w-full h-full object-cover rounded-lg"
              />
            </div>

            {/* Name and skill level */}
            <div className="text-center">
              <h3 className="text-sm font-semibold text-gray-800">
                {passion.name}
              </h3>
              <span className="text-xs text-gray-600 mt-1 block">
                {passion.skillLevel}
              </span>
            </div>

            {/* Mobile-friendly skill indicator */}
            <div className="md:hidden mt-2 flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((dot) => (
                <div
                  key={dot}
                  className={`w-1.5 h-1.5 rounded-full ${
                    dot <=
                    {
                      Beginner: 1,
                      Foundation: 2,
                      Intermediate: 3,
                      Advance: 4,
                      Pro: 5,
                    }[passion.skillLevel]
                      ? "bg-gray-800"
                      : "bg-gray-200"
                  }`}
                />
              ))}
            </div>

            {/* Desktop skill bar */}
            <div className="hidden md:flex items-center gap-1 mt-2">
              {[1, 2, 3, 4, 5].map((level) => (
                <div
                  key={level}
                  className={`h-1.5 w-4 rounded-full transition-colors ${
                    level <=
                    {
                      Beginner: 1,
                      Foundation: 2,
                      Intermediate: 3,
                      Advance: 4,
                      Pro: 5,
                    }[passion.skillLevel]
                      ? "bg-gray-800"
                      : "bg-gray-200"
                  }`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProfilePassionsGrid;
